import { useCallback } from 'react';
import { toast } from 'sonner';
import type { TFunction } from 'i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import type { FieldValues } from 'react-hook-form';
import type { ApiError } from '@/lib/agent.types';
import { logger } from '@/lib/logger';
import { translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';

export interface UseCrudHandlersOptions<
  TEntity,
  TCreateData extends FieldValues,
  TUpdateData extends FieldValues,
  TCreateResponse = TEntity,
  TUpdateResponse = TEntity,
  TDeleteResponse = void
> {
  /**
   * Create mutation from react-query
   * Optional: If not provided, create operations will show an error
   */
  createMutation?: UseMutationResult<TCreateResponse, Error, TCreateData>;

  /**
   * Update mutation from react-query
   * Optional: If not provided, update operations will show an error
   */
  updateMutation?: UseMutationResult<TUpdateResponse, Error, { id: string; data: TUpdateData }>;

  /**
   * Delete mutation from react-query
   * Optional: If not provided, delete operations will show an error
   */
  deleteMutation?: UseMutationResult<TDeleteResponse, Error, string>;

  /**
   * Callback when form submission succeeds
   */
  onFormSuccess?: () => void;

  /**
   * Callback when delete succeeds
   */
  onDeleteSuccess?: () => void;

  /**
   * Callback when delete fails (receives error message)
   */
  onDeleteError?: (errorMessage: string) => void;

  /**
   * Entity being edited (if in edit mode)
   */
  editingEntity?: TEntity & { _id: string };

  /**
   * ID of entity being deleted
   */
  deletingEntityId?: string | null;

  /**
   * Translation function for error messages
   */
  t: TFunction;
}

/**
 * Provides reusable handlers for CRUD operations with consistent error handling
 *
 * @example
 * const { handleFormSubmit, handleDeleteConfirm } = useCrudHandlers({
 *   createMutation,
 *   updateMutation,
 *   deleteMutation,
 *   editingEntity,
 *   deletingEntityId,
 *   t,
 *   onFormSuccess: () => {
 *     closeFormDialog();
 *   },
 *   onDeleteSuccess: () => {
 *     closeDeleteDialog();
 *   }
 * });
 */
export function useCrudHandlers<
  TEntity,
  TCreateData extends FieldValues,
  TUpdateData extends FieldValues,
  TCreateResponse = TEntity,
  TUpdateResponse = TEntity,
  TDeleteResponse = void
>({
  createMutation,
  updateMutation,
  deleteMutation,
  onFormSuccess,
  onDeleteSuccess,
  onDeleteError,
  editingEntity,
  deletingEntityId,
  t
}: UseCrudHandlersOptions<
  TEntity,
  TCreateData,
  TUpdateData,
  TCreateResponse,
  TUpdateResponse,
  TDeleteResponse
>) {
  /**
   * Helper to show error when operation is not supported
   */
  const showOperationNotSupported = useCallback(
    (errorCallback?: (msg: string) => void) => {
      const errorMessage = t('errors.operationNotSupported', {
        defaultValue: 'This operation is not supported.'
      });
      if (errorCallback) {
        errorCallback(errorMessage);
      } else {
        toast.error(errorMessage);
      }
    },
    [t]
  );

  /**
   * Handle form submission (create or update)
   *
   * Errors propagate to the caller: FormDialog owns the submit lifecycle and
   * routes an ApiError onto the form (field errors inline, everything else in
   * the dialog alert).
   */
  const handleFormSubmit = useCallback(
    async (data: TCreateData | TUpdateData) => {
      if (editingEntity) {
        if (!updateMutation) {
          showOperationNotSupported();
          return;
        }
        await updateMutation.mutateAsync({
          id: editingEntity._id,
          data: data as TUpdateData
        });
      } else {
        if (!createMutation) {
          showOperationNotSupported();
          return;
        }
        await createMutation.mutateAsync(data as TCreateData);
      }

      onFormSuccess?.();
    },
    [editingEntity, updateMutation, createMutation, onFormSuccess, showOperationNotSupported]
  );

  /**
   * Handle delete confirmation
   *
   * Shows error toast on failure with translated message
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingEntityId) return;

    if (!deleteMutation) {
      showOperationNotSupported(onDeleteError);
      return;
    }

    try {
      await deleteMutation.mutateAsync(deletingEntityId);

      // Success callback
      onDeleteSuccess?.();
    } catch (error) {
      logger.error('Failed to delete entity:', error);

      const apiError = error as ApiError;
      const errorMessage = translateErrorMessage(apiError, t);

      // Error callback or toast
      if (onDeleteError) {
        onDeleteError(errorMessage);
      } else {
        toastRequestError(error, errorMessage);
      }
    }
  }, [
    deletingEntityId,
    deleteMutation,
    onDeleteSuccess,
    onDeleteError,
    showOperationNotSupported,
    t
  ]);

  return {
    handleFormSubmit,
    handleDeleteConfirm,
    isDeleting: deleteMutation?.isPending ?? false
  };
}
